import type { ClipId, OpId, ProjectId, SourceId, TrackId } from "./ids.ts";
import type { Op, OpParams, OpRange } from "./ops.ts";

import type { MediaKind, Source } from "./source.ts";

export const SCHEMA_VERSION = 1;

export type ImageFormat = "png" | "jpeg" | "webp" | "avif";
export type AudioFormat = "wav" | "flac" | "opus" | "aac";
export type VideoFormat = "mp4" | "webm" | "gif";

export type OutputSpec =
  | { readonly kind: "image"; readonly format: ImageFormat; readonly quality: number }
  | { readonly kind: "audio"; readonly format: AudioFormat; readonly bitrateKbps: number }
  | { readonly kind: "video"; readonly format: VideoFormat; readonly bitrateKbps: number };

export type Clip = {
  readonly id: ClipId;
  readonly sourceId: SourceId;
  /** Source-local range. A still ignores `inSec` and uses `outSec` as its display length. */
  readonly inSec: number;
  readonly outSec: number;
  /** Position on the track. */
  readonly atSec: number;
  readonly ops: readonly Op[];
};

export type Track = {
  readonly id: TrackId;
  readonly kind: MediaKind;
  readonly muted: boolean;
  readonly clips: readonly Clip[];
};

export type Project = {
  readonly schemaVersion: typeof SCHEMA_VERSION;
  readonly id: ProjectId;
  readonly name: string;
  readonly kind: MediaKind;
  readonly sources: readonly Source[];
  readonly tracks: readonly Track[];
  readonly output: OutputSpec;
  /** ISO 8601 in UTC. */
  readonly createdAt: string;
  readonly updatedAt: string;
};

export const DEFAULT_OUTPUT: { readonly [K in MediaKind]: OutputSpec } = {
  image: { kind: "image", format: "png", quality: 0.92 },
  audio: { kind: "audio", format: "wav", bitrateKbps: 192 },
  video: { kind: "video", format: "mp4", bitrateKbps: 6000 },
};

export type CreateProjectInput = {
  readonly id: ProjectId;
  readonly name: string;
  readonly kind: MediaKind;
  readonly now: string;
};

export function createProject(input: CreateProjectInput): Project {
  return {
    schemaVersion: SCHEMA_VERSION,
    id: input.id,
    name: input.name,
    kind: input.kind,
    sources: [],
    tracks: [],
    output: DEFAULT_OUTPUT[input.kind],
    createdAt: input.now,
    updatedAt: input.now,
  };
}

export function findClip(project: Project, clipId: ClipId): Clip | null {
  for (const track of project.tracks) {
    const clip = track.clips.find((candidate) => candidate.id === clipId);
    if (clip) return clip;
  }
  return null;
}

export function findSource(project: Project, sourceId: SourceId): Source | null {
  return project.sources.find((source) => source.id === sourceId) ?? null;
}

export function findOp(project: Project, opId: OpId): Op | null {
  for (const track of project.tracks) {
    for (const clip of track.clips) {
      const op = clip.ops.find((candidate) => candidate.id === opId);
      if (op) return op;
    }
  }
  return null;
}

/** The clip a simple-mode editor works on: the first clip of the first track. */
export function primaryClip(project: Project): Clip | null {
  return project.tracks[0]?.clips[0] ?? null;
}

function mapClips(project: Project, transform: (clip: Clip) => Clip): Project {
  return {
    ...project,
    tracks: project.tracks.map((track) => ({ ...track, clips: track.clips.map(transform) })),
  };
}

function mapOneClip(project: Project, clipId: ClipId, transform: (clip: Clip) => Clip): Project {
  return mapClips(project, (clip) => (clip.id === clipId ? transform(clip) : clip));
}

function mapClipContainingOp(
  project: Project,
  opId: OpId,
  transform: (clip: Clip) => Clip,
): Project {
  return mapClips(project, (clip) =>
    clip.ops.some((op) => op.id === opId) ? transform(clip) : clip,
  );
}

export function addSource(project: Project, source: Source): Project {
  return { ...project, sources: [...project.sources, source] };
}

export function addTrack(project: Project, track: Track): Project {
  return { ...project, tracks: [...project.tracks, track] };
}

export function addClip(project: Project, trackId: TrackId, clip: Clip): Project {
  return {
    ...project,
    tracks: project.tracks.map((track) =>
      track.id === trackId ? { ...track, clips: [...track.clips, clip] } : track,
    ),
  };
}

export function addOp(project: Project, clipId: ClipId, op: Op): Project {
  return mapOneClip(project, clipId, (clip) => ({ ...clip, ops: [...clip.ops, op] }));
}

export function updateOp(project: Project, opId: OpId, params: OpParams): Project {
  return mapClipContainingOp(project, opId, (clip) => ({
    ...clip,
    ops: clip.ops.map((op) =>
      op.id === opId
        ? // The range belongs to the step, not its parameters, so it survives an edit.
          { ...params, id: op.id, enabled: op.enabled, ...(op.range ? { range: op.range } : {}) }
        : op,
    ),
  }));
}

/** Limits a step to part of the material, or restores it to all of it with null. */
export function setOpRange(project: Project, opId: OpId, range: OpRange | null): Project {
  return mapClipContainingOp(project, opId, (clip) => ({
    ...clip,
    ops: clip.ops.map((op) => {
      if (op.id !== opId) return op;
      const { range: existing, ...rest } = op;
      void existing;
      return range ? { ...rest, range } : rest;
    }),
  }));
}

/**
 * Drops a step and everything applied after it. The record of what was done only runs one
 * way, so the way back is to unwind it rather than to pick pieces out of the middle.
 */
export function revertToOp(project: Project, opId: OpId): Project {
  return mapClipContainingOp(project, opId, (clip) => {
    const index = clip.ops.findIndex((op) => op.id === opId);
    return index === -1 ? clip : { ...clip, ops: clip.ops.slice(0, index + 1) };
  });
}

export function setOpEnabled(project: Project, opId: OpId, enabled: boolean): Project {
  return mapClipContainingOp(project, opId, (clip) => ({
    ...clip,
    ops: clip.ops.map((op) => (op.id === opId ? { ...op, enabled } : op)),
  }));
}

export function removeOp(project: Project, opId: OpId): Project {
  return mapClipContainingOp(project, opId, (clip) => ({
    ...clip,
    ops: clip.ops.filter((op) => op.id !== opId),
  }));
}

export function moveOp(project: Project, opId: OpId, toIndex: number): Project {
  return mapClipContainingOp(project, opId, (clip) => {
    const from = clip.ops.findIndex((op) => op.id === opId);
    const moved = clip.ops[from];
    if (!moved) return clip;
    const rest = clip.ops.filter((op) => op.id !== opId);
    const target = Math.min(Math.max(toIndex, 0), rest.length);
    return { ...clip, ops: [...rest.slice(0, target), moved, ...rest.slice(target)] };
  });
}

/**
 * Swaps a clip's material for a rendered version of itself and drops the steps that
 * produced it, so a long chain becomes a starting point instead of work repeated on
 * every play. Undo restores both, and the old bytes are left in place.
 */
export function replaceClipSource(
  project: Project,
  clipId: ClipId,
  source: Source,
  durationSec: number,
): Project {
  const clip = findClip(project, clipId);
  if (!clip) return project;
  const sources = project.sources.map((existing) =>
    existing.id === clip.sourceId ? source : existing,
  );
  return mapOneClip({ ...project, sources }, clipId, (current) => ({
    ...current,
    sourceId: source.id,
    inSec: 0,
    outSec: durationSec,
    ops: [],
  }));
}

export function setTrimSec(
  project: Project,
  clipId: ClipId,
  inSec: number,
  outSec: number,
): Project {
  return mapOneClip(project, clipId, (clip) => ({ ...clip, inSec, outSec }));
}

export function setOutput(project: Project, output: OutputSpec): Project {
  return { ...project, output };
}

export function touch(project: Project, now: string): Project {
  return { ...project, updatedAt: now };
}

/** Ops that actually reach an engine: enabled, in authoring order. */
export function activeOps(clip: Clip): readonly Op[] {
  return clip.ops.filter((op) => op.enabled);
}
